import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown, Sparkles } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`mask-line ${className}`}>
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const CeramicTiles = ({ y }) => {
  const tiles = [
    { top: "10%", left: "6%", w: 70, h: 70, rot: -8, delay: 0.2 },
    { top: "22%", left: "88%", w: 46, h: 46, rot: 14, delay: 0.5 },
    { top: "62%", left: "4%", w: 54, h: 54, rot: 20, delay: 0.7 },
    { top: "76%", left: "92%", w: 80, h: 80, rot: -14, delay: 0.4 },
    { top: "42%", left: "94%", w: 36, h: 36, rot: 24, delay: 0.9 },
    { top: "86%", left: "48%", w: 44, h: 44, rot: 6, delay: 1.1 },
  ];
  return (
    <motion.div style={{ y }} className="absolute inset-0 pointer-events-none">
      {tiles.map((t, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.6, rotate: 0 }}
          animate={{ opacity: 0.9, scale: 1, rotate: t.rot }}
          transition={{ duration: 1.2, ease: EASE, delay: t.delay }}
          className="tile"
          style={{
            top: t.top,
            left: t.left,
            width: t.w,
            height: t.h,
          }}
        />
      ))}
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const tilesY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const headingY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] overflow-hidden pt-28 pb-20"
      data-testid="hero-section"
    >
      {/* Base grid */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 ceramic-grid opacity-40" />

      {/* Terracotta glow */}
      <div
        className="absolute inset-x-0 top-0 h-[70vh] pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 500px at 50% 0%, rgba(200,90,50,0.18) 0%, transparent 70%)",
        }}
      />

      <CeramicTiles y={tilesY} />

      <div className="relative z-10 max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Location badge */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="inline-flex items-center gap-2 border border-[color:var(--mati-border)] bg-white/60 backdrop-blur-sm pl-3 pr-4 py-1.5 rounded-full"
          data-testid="hero-location-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--mati-clay)] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[color:var(--mati-clay)]" />
          </span>
          <MapPin className="w-3.5 h-3.5 text-[color:var(--mati-forest)]" />
          <span className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--mati-forest)]">
            Morbi · Gujarat · 22.82°N 70.83°E
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          style={{ y: headingY }}
          className="mt-8 font-display font-extrabold text-[13vw] sm:text-[11vw] lg:text-[9.5vw] leading-[0.92] tracking-[-0.03em] text-[color:var(--mati-forest)]"
          data-testid="hero-heading"
        >
          <div>
            <MaskedLine delay={0.15}>The&nbsp;Soil.</MaskedLine>
          </div>
          <div>
            <MaskedLine delay={0.3}>The&nbsp;City.</MaskedLine>
          </div>
          <div className="relative inline-block">
            <MaskedLine delay={0.45} className="italic">
              <span className="font-display" style={{ fontStyle: "italic" }}>
                The{" "}
              </span>
              <span style={{ color: "var(--mati-clay)", fontStyle: "italic" }}>
                Citizen
              </span>
              <span style={{ fontStyle: "italic" }}>.</span>
            </MaskedLine>
          </div>
        </motion.h1>

        {/* Sub */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="col-span-12 lg:col-span-6 font-sub text-base sm:text-lg text-[color:var(--mati-muted)] max-w-xl leading-relaxed"
          >
            A citizen-led civic renewal movement to make our ceramic city the
            cleanest in Saurashtra — through awareness, participation, and
            field-tested waste management methods.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.0 }}
            className="col-span-12 lg:col-span-6 flex flex-col sm:flex-row gap-3 sm:justify-end"
          >
            <a
              href="#methods"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] px-6 py-4 rounded-full font-sub text-[13px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-clay)] transition-all"
            >
              Explore the Playbook
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </a>
            <a
              href="#journey"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 border border-[color:var(--mati-forest)] text-[color:var(--mati-forest)] px-6 py-4 rounded-full font-sub text-[13px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-forest)] hover:text-[color:var(--mati-sand)] transition-all"
            >
              <Sparkles className="w-4 h-4" /> Our Journey
            </a>
          </motion.div>
        </div>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 1.15 }}
          className="mt-20 grid grid-cols-3 border-t border-[color:var(--mati-border)]"
        >
          {[
            {
              k: "85",
              suffix: "th",
              label: "National Rank · Swachh Survekshan",
              testid: "hero-stat-rank-national",
            },
            {
              k: "7",
              suffix: "th",
              label: "Gujarat State Rank",
              testid: "hero-stat-rank-state",
            },
            {
              k: "248",
              suffix: "TPD",
              label: "Waste Generated Daily",
              testid: "hero-stat-waste",
            },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={s.testid}
              className={`px-4 sm:px-6 pt-6 pb-2 ${
                i < 2 ? "border-r border-[color:var(--mati-border)]" : ""
              }`}
            >
              <div className="flex items-baseline gap-1">
                <span className="font-display font-extrabold text-[color:var(--mati-forest)] text-4xl sm:text-5xl lg:text-6xl leading-none">
                  {s.k}
                </span>
                <span className="font-mono-ui text-[10px] tracking-widest text-[color:var(--mati-clay)]">
                  {s.suffix}
                </span>
              </div>
              <div className="mt-3 font-mono-ui text-[10px] uppercase tracking-[0.22em] text-[color:var(--mati-muted)] max-w-[180px]">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
